import ReactMarkdown from "react-markdown";
import React from "react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type {
  ArtifactRendererDocument,
  CustomAppArtifactDocument,
  ScientificUiArtifactDocument,
  ScientificUiBindings,
  ScientificUiCatalog,
  ScientificUiChartNode,
  ScientificUiColumn,
  ScientificUiDataTableNode,
  ScientificUiFilterBinding,
  ScientificUiFilterOperator,
  ScientificUiNode,
  ScientificUiPlotlyVolcanoChartNode,
  ScientificUiPrimitive,
  ScientificUiRow,
  ScientificUiSelection,
  ScientificUiSelectionFidelity,
  ScientificUiTextNode,
  ScientificUiValue,
} from "./types";

export const SCIENTIFIC_UI_MAX_INLINE_ROWS = 250;
export const CUSTOM_APP_MAX_SRCDOC_BYTES = 64 * 1024;
const SCIENTIFIC_UI_STABLE_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
const SCIENTIFIC_UI_MAX_VEGA_SPEC_BYTES = 64 * 1024;
const SCIENTIFIC_UI_VEGA_ROW_ID = "__scientific_ui_row_id";
const SCIENTIFIC_UI_VEGA_SELECTION = "scientificUiSelection";
const VEGA_VENDOR_SCRIPTS = [
  "vega.min.js",
  "vega-lite.min.js",
  "vega-embed.min.js",
] as const;

type RawRecord = Record<string, unknown>;
type ScientificUiChartRuntimeNode = ScientificUiChartNode & {
  spec?: RawRecord;
  specError?: string;
};
type ScientificUiSelectableRowsNode =
  | ScientificUiDataTableNode
  | ScientificUiChartNode
  | ScientificUiPlotlyVolcanoChartNode;

const PlotlyVolcanoChart = React.lazy(() => import("./PlotlyVolcanoChart"));

interface VegaViewRuntime {
  addEventListener(
    type: "click",
    listener: (
      event: Event,
      item: { datum?: unknown } | null,
    ) => void,
  ): void;
  removeEventListener(
    type: "click",
    listener: (
      event: Event,
      item: { datum?: unknown } | null,
    ) => void,
  ): void;
}

interface VegaEmbedResult {
  view: VegaViewRuntime;
  finalize(): void;
}

type VegaEmbedRuntime = (
  element: HTMLElement,
  spec: RawRecord,
  options: RawRecord,
) => Promise<VegaEmbedResult>;

let vegaRuntimePromise: Promise<VegaEmbedRuntime> | undefined;

export interface ScientificUiKernelProps {
  artifactId: string;
  document: ArtifactRendererDocument;
  onSelectionChange(selection: ScientificUiSelection): void;
  renderArtifact?(artifactRef: string): ReactNode;
  resolveDataRows?(artifactRef: string): ScientificUiRow[] | undefined;
  requestArtifactDetail?(artifactId: string): void;
}

interface CatalogRendererProps {
  artifactId: string;
  document: ScientificUiArtifactDocument;
  onSelectionChange(selection: ScientificUiSelection): void;
  renderArtifact?: (artifactRef: string) => ReactNode;
  resolveDataRows?: (artifactRef: string) => ScientificUiRow[] | undefined;
  requestArtifactDetail?: (artifactId: string) => void;
}

interface CustomAppRendererProps {
  artifactId: string;
  document: CustomAppArtifactDocument;
  onSelectionChange(selection: ScientificUiSelection): void;
}

interface BridgeSelectionMessage {
  channel: "cadence.custom-app";
  version: 1;
  type: "selection";
  componentId: string;
  selectionId?: string;
  value?: ScientificUiValue;
}

interface BridgeResizeMessage {
  channel: "cadence.custom-app";
  version: 1;
  type: "resize";
  height: number;
}

type BridgeMessage = BridgeSelectionMessage | BridgeResizeMessage;

type StateMap = Record<string, ScientificUiValue>;

const rendererRegistry: Record<
  "scientific-ui" | "custom-app",
  React.ComponentType<ScientificUiKernelProps>
> = {
  "scientific-ui": CatalogRenderer as React.ComponentType<ScientificUiKernelProps>,
  "custom-app": CustomAppRenderer as React.ComponentType<ScientificUiKernelProps>,
};

function vegaVendorPath(file: (typeof VEGA_VENDOR_SCRIPTS)[number]) {
  const base = import.meta.env.BASE_URL.endsWith("/")
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  return `${base}vendor/vega/${file}`;
}

function loadVegaVendorScript(file: (typeof VEGA_VENDOR_SCRIPTS)[number]) {
  const src = vegaVendorPath(file);
  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-scientific-ui-vega="${file}"]`,
    );
    const script = existing ?? document.createElement("script");
    const cleanup = () => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    };
    const onLoad = () => {
      cleanup();
      script.dataset.scientificUiVegaLoaded = "true";
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Unable to load self-hosted Vega runtime: ${file}`));
    };

    if (script.dataset.scientificUiVegaLoaded === "true") {
      resolve();
      return;
    }
    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
    if (!existing) {
      script.src = src;
      script.async = false;
      script.dataset.scientificUiVega = file;
      document.head.appendChild(script);
    }
  });
}

function loadVegaRuntime(): Promise<VegaEmbedRuntime> {
  if (!vegaRuntimePromise) {
    vegaRuntimePromise = (async () => {
      for (const script of VEGA_VENDOR_SCRIPTS) {
        await loadVegaVendorScript(script);
      }
      const globals = globalThis as typeof globalThis & {
        vega?: { version?: string };
        vegaLite?: { version?: string };
        vegaEmbed?: VegaEmbedRuntime & { version?: string };
      };
      if (
        globals.vega?.version !== "6.2.0" ||
        globals.vegaLite?.version !== "6.4.3" ||
        globals.vegaEmbed?.version !== "7.1.0" ||
        typeof globals.vegaEmbed !== "function"
      ) {
        throw new Error("Self-hosted Vega runtime version mismatch.");
      }
      return globals.vegaEmbed;
    })();
  }
  return vegaRuntimePromise;
}

const BRIDGE_SCRIPT = `
<script>
(function () {
  var channel = "cadence.custom-app";
  var send = function (message) {
    window.parent.postMessage(Object.assign({ channel: channel, version: 1 }, message), "*");
  };
  window.CadenceCustomApp = Object.freeze({
    select: function (componentId, value, selectionId) {
      if (typeof componentId !== "string" || !componentId) return;
      send({ type: "selection", componentId: componentId, value: value, selectionId: selectionId });
    },
    resize: function (height) {
      var next = Number(height);
      if (Number.isFinite(next)) send({ type: "resize", height: next });
    }
  });
  var resize = function () {
    window.CadenceCustomApp.resize(document.documentElement.scrollHeight || document.body.scrollHeight || 180);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", resize, { once: true });
  else resize();
  if (typeof ResizeObserver === "function") {
    new ResizeObserver(resize).observe(document.documentElement);
  }
  send({ type: "lifecycle", phase: "ready" });
})();
</script>
`;

export function rendererDocumentFromRaw(
  raw: unknown,
): ArtifactRendererDocument | undefined {
  if (!isRecord(raw) || typeof raw.renderer !== "string") return undefined;
  if (raw.renderer === "scientific-ui") {
    if (
      raw.cadence_artifact_version !== 1 ||
      raw.version !== 1 ||
      typeof raw.id !== "string" ||
      typeof raw.revision !== "number" ||
      typeof raw.root !== "string"
    ) {
      return undefined;
    }
    return {
      cadence_artifact_version: 1,
      version: 1,
      renderer: "scientific-ui",
      id: raw.id,
      revision: raw.revision,
      title: typeof raw.title === "string" ? raw.title : undefined,
      root: raw.root,
      bindings: bindingsFromRaw(raw.bindings),
      catalog: scientificUiCatalogFromRaw(raw.catalog),
    };
  }
  if (raw.renderer === "custom-app") {
    if (
      raw.cadence_artifact_version !== 1 ||
      raw.version !== 1 ||
      typeof raw.id !== "string" ||
      typeof raw.revision !== "number" ||
      typeof raw.srcdoc !== "string" ||
      typeof raw.entrypoint !== "string" ||
      customAppSrcdocBytes(raw.srcdoc) > CUSTOM_APP_MAX_SRCDOC_BYTES ||
      !sandboxFromRaw(raw.sandbox)
    ) {
      return undefined;
    }
    return {
      cadence_artifact_version: 1,
      version: 1,
      renderer: "custom-app",
      id: raw.id,
      revision: raw.revision,
      title: typeof raw.title === "string" ? raw.title : undefined,
      srcdoc: raw.srcdoc,
      entrypoint: raw.entrypoint,
      sandbox: {
        allow: ["scripts"],
        allow_same_origin: false,
        allow_top_navigation: false,
      },
      bindings: bindingsFromRaw(raw.bindings),
    };
  }
  if (raw.renderer === "curve" || raw.renderer === "flow") {
    return undefined;
  }
  return {
    renderer: raw.renderer,
    ...raw,
  };
}

export function scientificUiRowsFromViewArtifact(
  raw: unknown,
): ScientificUiRow[] | undefined {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (!isRecord(value) || value.kind !== "scientific_view_data") return undefined;
  const view = value.view;
  if (!isRecord(view) || !Array.isArray(view.rows)) return undefined;
  const recordRows = rowsFromRaw(view.rows);
  if (recordRows) return recordRows;
  if (
    view.view_type !== "table" ||
    !Array.isArray(view.columns) ||
    !view.columns.every((column) => typeof column === "string") ||
    view.rows.length > SCIENTIFIC_UI_MAX_INLINE_ROWS
  ) {
    return undefined;
  }
  const columns = view.columns as string[];
  const rows = view.rows.flatMap((row, index) => {
    if (!Array.isArray(row)) return [];
    const values = Object.fromEntries(
      columns.map((column, columnIndex) => [column, row[columnIndex] ?? null]),
    );
    if (!isRecord(values) || !isScientificUiValue(values)) return [];
    return [
      {
        id: `row-${String(index + 1).padStart(4, "0")}`,
        values: values as Record<string, ScientificUiValue>,
      },
    ];
  });
  return rows.length === view.rows.length ? rows : undefined;
}

export function ScientificUiKernel({
  artifactId,
  document,
  onSelectionChange,
  renderArtifact,
  resolveDataRows,
  requestArtifactDetail,
}: ScientificUiKernelProps) {
  const renderer =
    document.renderer === "scientific-ui" || document.renderer === "custom-app"
      ? rendererRegistry[document.renderer]
      : undefined;
  if (!renderer) {
    return (
      <UnsupportedRenderer
        renderer={document.renderer}
        reason="This Artifact renderer is not enabled in the Scientific UI Kernel."
      />
    );
  }
  return React.createElement(renderer, {
    key: `${artifactId}:${document.revision}`,
    artifactId,
    document,
    onSelectionChange,
    renderArtifact,
    resolveDataRows,
    requestArtifactDetail,
  });
}

function CatalogRenderer({
  artifactId,
  document,
  onSelectionChange,
  renderArtifact,
  resolveDataRows,
  requestArtifactDetail,
}: CatalogRendererProps) {
  const [values, setValues] = useState<StateMap>(() =>
    initialState(document.catalog?.root),
  );
  const valuesRef = useRef(values);
  const [selectedRows, setSelectedRows] = useState<Record<string, string[]>>(
    {},
  );
  const selectedRowsRef = useRef(selectedRows);
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});
  const dataRefs = useMemo(() => catalogDataRefs(document), [document]);

  useEffect(() => {
    if (!requestArtifactDetail) return;
    dataRefs.forEach((ref) => {
      if (resolveDataRows?.(ref)) return;
      const artifactId = normalizeArtifactReference(ref);
      if (artifactId) requestArtifactDetail(artifactId);
    });
  }, [dataRefs, requestArtifactDetail, resolveDataRows]);

  const emit = useCallback(
    (
      node: ScientificUiNode,
      interaction: ScientificUiSelection["interaction"],
      value?: ScientificUiValue,
      ids: string[] = [node.id],
      nextValues: StateMap = valuesRef.current,
      nextSelectedRows: Record<string, string[]> = selectedRowsRef.current,
    ) => {
      onSelectionChange({
        renderer: "scientific-ui",
        artifactId,
        ids,
        componentId: node.id,
        interaction,
        value,
        actionId: node.type === "Button" ? node.actionId : undefined,
        fidelity: catalogSelectionFidelity(
          document,
          node,
          ids,
          nextValues,
          nextSelectedRows,
        ),
      });
    },
    [artifactId, document, onSelectionChange],
  );

  const setNodeValue = useCallback(
    (node: ScientificUiNode, value: ScientificUiValue) => {
      const nextValues = { ...valuesRef.current, [node.id]: value };
      valuesRef.current = nextValues;
      setValues(nextValues);
      emit(node, "change", value, [node.id], nextValues);
    },
    [emit],
  );

  const toggleRow = useCallback(
    (node: ScientificUiSelectableRowsNode, row: ScientificUiRow) => {
      const selected = selectedRowsRef.current[node.id] ?? [];
      const next = selected.includes(row.id)
        ? selected.filter((id) => id !== row.id)
        : [...selected, row.id];
      const nextSelectedRows = {
        ...selectedRowsRef.current,
        [node.id]: next,
      };
      selectedRowsRef.current = nextSelectedRows;
      setSelectedRows(nextSelectedRows);
      emit(
        node,
        "select",
        row.values,
        [node.id, ...next],
        valuesRef.current,
        nextSelectedRows,
      );
    },
    [emit],
  );

  const selectRows = useCallback(
    (node: ScientificUiSelectableRowsNode, rows: ScientificUiRow[]) => {
      const next = [...new Set(rows.map((row) => row.id))];
      const nextSelectedRows = {
        ...selectedRowsRef.current,
        [node.id]: next,
      };
      selectedRowsRef.current = nextSelectedRows;
      setSelectedRows(nextSelectedRows);
      emit(
        node,
        "select",
        rows.length === 1 ? rows[0]?.values : { selectedRowCount: next.length },
        [node.id, ...next],
        valuesRef.current,
        nextSelectedRows,
      );
    },
    [emit],
  );

  if (!document.catalog?.root) {
    return (
      <UnsupportedRenderer
        renderer="scientific-ui"
        reason={`Catalog root "${document.root}" is not available in this Artifact detail.`}
      />
    );
  }

  return (
    <section
      className="scientific-ui-kernel"
      data-testid="scientific-ui-kernel"
      data-renderer-revision={document.revision}
      aria-label={document.title ?? "Scientific UI Artifact"}
    >
      <div className="scientific-ui-head">
        <div>
          <span className="scientific-kicker">Scientific UI / Catalog V0</span>
          <h4>{document.title ?? document.id}</h4>
        </div>
        <span
          className="scientific-ui-revision"
          data-testid="scientific-ui-renderer-revision"
        >
          View r{document.revision}
        </span>
        <span className="scientific-ui-root">{document.root}</span>
      </div>
      <div className="scientific-ui-canvas">
        {renderNode(
          document.catalog.root,
          values,
          selectedRows,
          activeTabs,
          setActiveTabs,
          setNodeValue,
          toggleRow,
          selectRows,
          emit,
          document,
          renderArtifact,
          resolveDataRows,
        )}
      </div>
    </section>
  );
}

function renderNode(
  node: ScientificUiNode,
  values: StateMap,
  selectedRows: Record<string, string[]>,
  activeTabs: Record<string, string>,
  setActiveTabs: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setNodeValue: (node: ScientificUiNode, value: ScientificUiValue) => void,
  toggleRow: (
    node: ScientificUiSelectableRowsNode,
    row: ScientificUiRow,
  ) => void,
  selectRows: (node: ScientificUiSelectableRowsNode, rows: ScientificUiRow[]) => void,
  emit: (
    node: ScientificUiNode,
    interaction: ScientificUiSelection["interaction"],
    value?: ScientificUiValue,
    ids?: string[],
  ) => void,
  document: ScientificUiArtifactDocument,
  renderArtifact?: (artifactRef: string) => ReactNode,
  resolveDataRows?: (artifactRef: string) => ScientificUiRow[] | undefined,
): ReactNode {
  switch (node.type) {
    case "Row":
    case "Column":
      return (
        <div
          className={`scientific-ui-node scientific-ui-${node.type.toLowerCase()}`}
          data-testid={`scientific-ui-node-${node.id}`}
          style={
            {
              "--scientific-ui-gap": `${Math.max(0, node.gap ?? 12)}px`,
            } as CSSProperties
          }
        >
          {node.children.map((child) => (
            <div key={child.id} className="scientific-ui-child">
              {renderNode(
                child,
                values,
                selectedRows,
                activeTabs,
                setActiveTabs,
                setNodeValue,
                toggleRow,
                selectRows,
                emit,
                document,
                renderArtifact,
                resolveDataRows,
              )}
            </div>
          ))}
        </div>
      );
    case "Tabs": {
      const activeId =
        activeTabs[node.id] ??
        node.defaultTabId ??
        node.tabs[0]?.id;
      const activeTab = node.tabs.find((tab) => tab.id === activeId) ?? node.tabs[0];
      return (
        <section
          className="scientific-ui-node scientific-ui-tabs"
          data-testid={`scientific-ui-node-${node.id}`}
        >
          <div className="scientific-ui-tab-list" role="tablist">
            {node.tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={tab.id === activeTab?.id}
                className={tab.id === activeTab?.id ? "is-active" : ""}
                onClick={() => {
                  setActiveTabs((current) => ({ ...current, [node.id]: tab.id }));
                  emit(node, "change", tab.id, [node.id, tab.id]);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {activeTab && (
            <div className="scientific-ui-tab-panel" role="tabpanel">
              {renderNode(
                activeTab.content,
                values,
                selectedRows,
                activeTabs,
                setActiveTabs,
                setNodeValue,
                toggleRow,
                selectRows,
                emit,
                document,
                renderArtifact,
                resolveDataRows,
              )}
            </div>
          )}
        </section>
      );
    }
    case "Text":
      return (
        <p
          className={`scientific-ui-node scientific-ui-text scientific-ui-tone-${node.tone ?? "default"}`}
          data-testid={`scientific-ui-node-${node.id}`}
        >
          {node.text}
        </p>
      );
    case "Markdown":
      return (
        <div
          className="scientific-ui-node scientific-ui-markdown"
          data-testid={`scientific-ui-node-${node.id}`}
        >
          <ReactMarkdown remarkPlugins={[]}>{node.content}</ReactMarkdown>
        </div>
      );
    case "Metric":
      return (
        <div
          className="scientific-ui-node scientific-ui-metric"
          data-testid={`scientific-ui-node-${node.id}`}
        >
          <span>{node.label}</span>
          <strong>
            {node.value}
            {node.unit && <small>{node.unit}</small>}
          </strong>
          {node.detail && <em>{node.detail}</em>}
        </div>
      );
    case "DataTable":
      return (
        <DataTableNode
          node={node}
          rows={rowsForNode(node, document, values, resolveDataRows)}
          selectedRows={selectedRows[node.id] ?? []}
          onRowSelect={(row) => toggleRow(node, row)}
        />
      );
    case "Slider": {
      const value =
        typeof values[node.id] === "number" ? Number(values[node.id]) : node.value;
      return (
        <label
          className="scientific-ui-node scientific-ui-control"
          data-testid={`scientific-ui-node-${node.id}`}
        >
          <span>
            {node.label} <output>{value}{node.unit ? ` ${node.unit}` : ""}</output>
          </span>
          <input
            type="range"
            min={node.min}
            max={node.max}
            step={node.step ?? 1}
            value={value}
            aria-label={node.label}
            onChange={(event) => setNodeValue(node, Number(event.target.value))}
          />
        </label>
      );
    }
    case "RangeSlider": {
      const stored = values[node.id];
      const value: [number, number] =
        Array.isArray(stored) && stored.length === 2
          ? [Number(stored[0]), Number(stored[1])]
          : node.value;
      return (
        <div
          className="scientific-ui-node scientific-ui-control scientific-ui-range"
          data-testid={`scientific-ui-node-${node.id}`}
        >
          <span>
            {node.label} <output>{value[0]} - {value[1]}{node.unit ? ` ${node.unit}` : ""}</output>
          </span>
          <input
            type="range"
            min={node.min}
            max={node.max}
            step={node.step ?? 1}
            value={value[0]}
            aria-label={`${node.label} minimum`}
            onChange={(event) =>
              setNodeValue(node, [
                Math.min(Number(event.target.value), value[1]),
                value[1],
              ])
            }
          />
          <input
            type="range"
            min={node.min}
            max={node.max}
            step={node.step ?? 1}
            value={value[1]}
            aria-label={`${node.label} maximum`}
            onChange={(event) =>
              setNodeValue(node, [
                value[0],
                Math.max(value[0], Number(event.target.value)),
              ])
            }
          />
        </div>
      );
    }
    case "Select": {
      const storedValue = values[node.id] ?? node.value;
      const selectedOption =
        node.options.find((option) =>
          scientificUiValuesEqual(option.value ?? option.id, storedValue),
        ) ?? node.options.find((option) => option.id === node.value);
      return (
        <label
          className="scientific-ui-node scientific-ui-control"
          data-testid={`scientific-ui-node-${node.id}`}
        >
          <span>{node.label}</span>
          <select
            value={selectedOption?.id ?? node.value}
            aria-label={node.label}
            onChange={(event) => {
              const option = node.options.find((item) => item.id === event.target.value);
              setNodeValue(node, option?.value ?? event.target.value);
            }}
          >
            {node.options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      );
    }
    case "CheckBox": {
      const checked = typeof values[node.id] === "boolean" ? Boolean(values[node.id]) : node.checked;
      return (
        <label
          className="scientific-ui-node scientific-ui-checkbox"
          data-testid={`scientific-ui-node-${node.id}`}
        >
          <input
            type="checkbox"
            checked={checked}
            aria-label={node.label}
            onChange={(event) => setNodeValue(node, event.target.checked)}
          />
          <span>{node.label}</span>
        </label>
      );
    }
    case "Button":
      return (
        <button
          className={`scientific-ui-node scientific-ui-button scientific-ui-button-${node.variant ?? "quiet"}`}
          data-testid={`scientific-ui-node-${node.id}`}
          type="button"
          onClick={() => emit(node, "activate", node.payload)}
        >
          {node.label}
        </button>
      );
    case "VegaLiteChart":
      return (
        <ChartNode
          node={node}
          rows={rowsForNode(node, document, values, resolveDataRows)}
          selectedRows={selectedRows[node.id] ?? []}
          onRowSelect={(row) => toggleRow(node, row)}
        />
      );
    case "PlotlyVolcanoChart": {
      const rows = rowsForNode(node, document, values, resolveDataRows);
      return (
        <React.Suspense
          fallback={
            <div
              className="scientific-ui-node scientific-ui-data-binding"
              data-testid={`scientific-ui-plotly-loading-${node.id}`}
            >
              Loading interactive chart.
            </div>
          }
        >
          <PlotlyVolcanoChart
            key={`cadence-volcano:${document.id}:${document.revision}:${node.id}`}
            id={node.id}
            title={node.title}
            rows={rows ?? []}
            log2FoldChange={node.log2FoldChange}
            adjustedPValue={node.adjustedPValue}
            fdrThreshold={node.fdrThreshold}
            foldChangeThreshold={node.foldChangeThreshold}
            selectedRowIds={selectedRows[node.id] ?? []}
            viewportStorageKey={`cadence.scientific-ui.volcano.${document.id}.${document.revision}.${node.id}`}
            onPointToggle={(rowId) => {
              const row = rows?.find((candidate) => candidate.id === rowId);
              if (row) toggleRow(node, row);
            }}
            onPointsSelect={(rowIds) =>
              selectRows(
                node,
                (rows ?? []).filter((row) => rowIds.includes(row.id)),
              )
            }
          />
        </React.Suspense>
      );
    }
    case "ArtifactViewer":
      return (
        <section
          className="scientific-ui-node scientific-ui-artifact-viewer"
          data-testid={`scientific-ui-node-${node.id}`}
        >
          <span>{node.title ?? "Artifact viewer"}</span>
          {renderArtifact?.(node.artifactRef) ?? (
            <p>Artifact binding unavailable: {node.artifactRef}</p>
          )}
        </section>
      );
  }
}

function DataTableNode({
  node,
  rows,
  selectedRows,
  onRowSelect,
}: {
  node: ScientificUiDataTableNode;
  rows?: ScientificUiRow[];
  selectedRows: string[];
  onRowSelect(row: ScientificUiRow): void;
}) {
  if (!rows) {
    return (
      <div className="scientific-ui-node scientific-ui-data-binding" data-testid={`scientific-ui-node-${node.id}`}>
        Data binding unavailable{node.binding ? `: ${node.binding}` : "."}
      </div>
    );
  }
  return (
    <div
      className="scientific-ui-node scientific-ui-table-wrap"
      data-testid={`scientific-ui-node-${node.id}`}
      role="region"
      aria-label="科学数据表格，可横向滚动"
      tabIndex={0}
    >
      <table className="scientific-ui-table">
        <thead>
          <tr>{node.columns.map((column) => <th key={column.id}>{column.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={selectedRows.includes(row.id) ? "is-selected" : ""}
              onClick={() => onRowSelect(row)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onRowSelect(row);
              }}
              tabIndex={0}
              aria-selected={selectedRows.includes(row.id)}
              data-testid={`scientific-ui-row-${row.id}`}
            >
              {node.columns.map((column) => (
                <td key={column.id}>{displayValue(row.values[column.id])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartNode({
  node,
  rows,
  selectedRows,
  onRowSelect,
}: {
  node: ScientificUiChartNode;
  rows?: ScientificUiRow[];
  selectedRows: string[];
  onRowSelect(row: ScientificUiRow): void;
}) {
  const runtimeNode = node as ScientificUiChartRuntimeNode;
  const runtimeRef = useRef<HTMLDivElement>(null);
  const onRowSelectRef = useRef(onRowSelect);
  onRowSelectRef.current = onRowSelect;
  const numericRows = useMemo(
    () =>
      (rows ?? [])
        .slice(0, SCIENTIFIC_UI_MAX_INLINE_ROWS)
        .map((row) => ({
          row,
          x: Number(row.values[node.x.field]),
          y: Number(row.values[node.y.field]),
        }))
        .filter((item) => Number.isFinite(item.x) && Number.isFinite(item.y)),
    [node.x.field, node.y.field, rows],
  );
  const [runtimeError, setRuntimeError] = useState<string | undefined>(
    runtimeNode.specError,
  );

  useEffect(() => {
    const element = runtimeRef.current;
    if (!element || !rows || !numericRows.length) return;
    element.replaceChildren();
    if (runtimeNode.specError) {
      setRuntimeError(runtimeNode.specError);
      return;
    }

    let disposed = false;
    let embedded: VegaEmbedResult | undefined;
    const onVegaClick = (
      _event: Event,
      item: { datum?: unknown } | null,
    ) => {
      const rowId = selectedVegaRowId(item?.datum);
      const selected = numericRows.find((item) => item.row.id === rowId);
      if (selected) onRowSelectRef.current(selected.row);
    };

    setRuntimeError(undefined);
    void loadVegaRuntime()
      .then((embed) =>
        embed(element, vegaLiteSpecForChart(runtimeNode, numericRows), {
          actions: false,
          defaultStyle: false,
          hover: false,
          mode: "vega-lite",
          renderer: "svg",
          tooltip: false,
        }),
      )
      .then((result) => {
        if (disposed) {
          result.finalize();
          return;
        }
        embedded = result;
        result.view.addEventListener("click", onVegaClick);
      })
      .catch((error: unknown) => {
        if (!disposed) {
          element.replaceChildren();
          setRuntimeError(
            error instanceof Error
              ? error.message
              : "Vega-Lite runtime failed to render the chart.",
          );
        }
      });

    return () => {
      disposed = true;
      if (embedded) {
        embedded.view.removeEventListener("click", onVegaClick);
        embedded.finalize();
      }
      element.replaceChildren();
    };
  }, [node.id, numericRows, rows, runtimeNode]);

  if (!rows) {
    return (
      <div className="scientific-ui-node scientific-ui-data-binding" data-testid={`scientific-ui-node-${node.id}`}>
        Chart binding unavailable{node.binding ? `: ${node.binding}` : "."}
      </div>
    );
  }
  if (!numericRows.length) {
    return (
      <div
        className="scientific-ui-node scientific-ui-fallback"
        data-testid={`scientific-ui-chart-fallback-${node.id}`}
        role="status"
      >
        <strong>Chart fallback:</strong> chart requires numeric x/y fields.
      </div>
    );
  }

  return (
    <figure className="scientific-ui-node scientific-ui-chart" data-testid={`scientific-ui-node-${node.id}`}>
      {node.title && <figcaption>{node.title}</figcaption>}
      <div
        ref={runtimeRef}
        className="scientific-ui-vega-runtime"
        data-testid={`scientific-ui-vega-runtime-${node.id}`}
        aria-label={node.title ?? "Scientific chart"}
      />
      {runtimeError && (
        <div
          className="scientific-ui-fallback"
          data-testid={`scientific-ui-chart-fallback-${node.id}`}
          role="status"
        >
          <strong>Chart fallback:</strong> {runtimeError}
        </div>
      )}
      <div
        className="scientific-ui-chart-accessible-list"
        role="list"
        aria-label={`${node.title ?? "Scientific chart"} data points`}
      >
        {numericRows.map((item) => {
          const label = `${item.row.id}: ${node.x.field} ${item.x}, ${node.y.field} ${item.y}`;
          return (
            <span key={item.row.id} role="listitem">
              <button
                type="button"
                className={
                  selectedRows.includes(item.row.id) ? "is-selected" : ""
                }
                aria-label={label}
                aria-pressed={selectedRows.includes(item.row.id)}
                data-testid={`scientific-ui-chart-point-${item.row.id}`}
                onClick={() => onRowSelect(item.row)}
              >
                {label}
              </button>
            </span>
          );
        })}
      </div>
    </figure>
  );
}

function vegaLiteSpecForChart(
  node: ScientificUiChartRuntimeNode,
  rows: Array<{ row: ScientificUiRow; x: number; y: number }>,
): RawRecord {
  const values = rows.map(({ row }) => ({
    ...row.values,
    [SCIENTIFIC_UI_VEGA_ROW_ID]: row.id,
  }));
  const encoding = {
    x: {
      field: node.x.field,
      type: "quantitative",
      title: node.x.label ?? node.x.field,
    },
    y: {
      field: node.y.field,
      type: "quantitative",
      title: node.y.label ?? node.y.field,
    },
  };
  const selectionName = chartSelectionName(node.id);
  const selection = {
    name: selectionName,
    select: {
      type: "point",
      on: "click",
      fields: [SCIENTIFIC_UI_VEGA_ROW_ID],
      toggle: "event.shiftKey",
    },
  };

  if (node.spec) {
    const existingParams = Array.isArray(node.spec.params)
      ? node.spec.params.filter(
          (param) => !isRecord(param) || param.name !== selectionName,
        )
      : [];
    const runtimeSpec: RawRecord = {
      ...node.spec,
      $schema: "https://vega.github.io/schema/vega-lite/v6.json",
      data: { values },
    };
    delete runtimeSpec.params;
    const selectionLayer = {
      params: [selection],
      mark: {
        type: "point",
        opacity: 0.001,
        size: 144,
        cursor: "pointer",
        tooltip: false,
        aria: false,
      },
      encoding,
    };
    const layers = runtimeSpec.layer;
    if (Array.isArray(layers)) {
      const [firstLayer, ...remainingLayers] = layers;
      const firstLayerRecord = firstLayer as RawRecord;
      const firstLayerParams = firstLayerRecord.params;
      runtimeSpec.layer = [
        {
          ...firstLayerRecord,
          ...(existingParams.length
            ? {
                params: [
                  ...(Array.isArray(firstLayerParams) ? firstLayerParams : []),
                  ...existingParams,
                ],
              }
            : {}),
        },
        ...remainingLayers,
        selectionLayer,
      ];
      return runtimeSpec;
    }
    const mark = runtimeSpec.mark;
    const specEncoding = runtimeSpec.encoding;
    delete runtimeSpec.mark;
    delete runtimeSpec.encoding;
    runtimeSpec.layer = [
      {
        mark,
        ...(isRecord(specEncoding) ? { encoding: specEncoding } : {}),
        ...(existingParams.length ? { params: existingParams } : {}),
      },
      selectionLayer,
    ];
    return runtimeSpec;
  }

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    width: "container",
    height: 220,
    autosize: { type: "fit", contains: "padding" },
    data: { values },
    ...(node.mark === "line"
      ? {
          layer: [
            {
              mark: { type: "line", point: false },
              encoding,
            },
            {
              params: [selection],
              mark: { type: "point", filled: true, size: 64, cursor: "pointer" },
              encoding,
            },
          ],
        }
      : {
          params: [selection],
          mark: { type: "point", filled: true, size: 64, cursor: "pointer" },
          encoding,
        }),
  };
}

function chartSelectionName(nodeId: string) {
  const suffix = nodeId.replace(/[^A-Za-z0-9_]/g, "_").slice(0, 40);
  return `${SCIENTIFIC_UI_VEGA_SELECTION}_${suffix || "chart"}`;
}

function selectedVegaRowId(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  const rowId = value[SCIENTIFIC_UI_VEGA_ROW_ID];
  return typeof rowId === "string" ? rowId : undefined;
}

function optionalVegaLiteSpecFromRaw(
  raw: unknown,
): Pick<ScientificUiChartRuntimeNode, "spec" | "specError"> {
  if (raw === undefined) return {};
  if (!isRecord(raw)) {
    return { specError: "optional Vega-Lite spec must be a JSON object." };
  }

  let encoded: string | undefined;
  try {
    encoded = JSON.stringify(raw);
  } catch {
    return { specError: "optional Vega-Lite spec is not valid JSON." };
  }
  if (
    encoded === undefined ||
    new TextEncoder().encode(encoded).byteLength >
      SCIENTIFIC_UI_MAX_VEGA_SPEC_BYTES
  ) {
    return {
      specError: `optional Vega-Lite spec exceeds ${SCIENTIFIC_UI_MAX_VEGA_SPEC_BYTES} bytes.`,
    };
  }

  const spec = JSON.parse(encoded) as unknown;
  if (!isRecord(spec)) {
    return { specError: "optional Vega-Lite spec must be a JSON object." };
  }
  const error = validateOptionalVegaLiteSpec(spec);
  return error ? { specError: error } : { spec };
}

function validateOptionalVegaLiteSpec(spec: RawRecord): string | undefined {
  if (
    spec.$schema !== undefined &&
    (typeof spec.$schema !== "string" ||
      !/^https:\/\/vega\.github\.io\/schema\/vega-lite\/v6(?:\.json)?$/i.test(
        spec.$schema,
      ))
  ) {
    return "optional Vega-Lite spec must use the Vega-Lite v6 schema.";
  }
  if (
    "concat" in spec ||
    "hconcat" in spec ||
    "vconcat" in spec ||
    "facet" in spec ||
    "repeat" in spec
  ) {
    return "optional Vega-Lite spec supports only point/line unit or layer views.";
  }
  if ("datasets" in spec) {
    return "optional Vega-Lite spec must not define named inline datasets.";
  }
  if (spec.params !== undefined && !Array.isArray(spec.params)) {
    return "optional Vega-Lite spec params must be an array.";
  }
  if (
    isRecord(spec.usermeta) &&
    Object.prototype.hasOwnProperty.call(spec.usermeta, "embedOptions")
  ) {
    return "optional Vega-Lite spec must not override runtime embed options.";
  }

  let markCount = 0;
  const visit = (
    value: unknown,
    path: string,
    depth: number,
  ): string | undefined => {
    if (depth > 24) {
      return "optional Vega-Lite spec nesting is too deep.";
    }
    if (Array.isArray(value)) {
      for (let index = 0; index < value.length; index += 1) {
        const error = visit(value[index], `${path}[${index}]`, depth + 1);
        if (error) return error;
      }
      return undefined;
    }
    if (!isRecord(value)) return undefined;

    for (const [key, child] of Object.entries(value)) {
      if (
        key !== "$schema" &&
        (key.toLowerCase() === "href" || key.toLowerCase().endsWith("url"))
      ) {
        return "optional Vega-Lite spec must not contain runtime URLs.";
      }
      if (key === "data" && path !== "$") {
        return "optional Vega-Lite spec data must be declared at the root.";
      }
      if (
        key === "values" &&
        Array.isArray(child) &&
        child.length > SCIENTIFIC_UI_MAX_INLINE_ROWS
      ) {
        return `optional Vega-Lite spec inline data exceeds ${SCIENTIFIC_UI_MAX_INLINE_ROWS} rows.`;
      }
      if (key === "layer" && (!Array.isArray(child) || child.length === 0)) {
        return "optional Vega-Lite spec layer must be a non-empty array.";
      }
      if (
        key === "layer" &&
        Array.isArray(child) &&
        !child.every(isRecord)
      ) {
        return "optional Vega-Lite spec layers must be JSON objects.";
      }
      if (
        key === "params" &&
        Array.isArray(child) &&
        child.some(
          (param) =>
            isRecord(param) &&
            typeof param.name === "string" &&
            param.name.startsWith(SCIENTIFIC_UI_VEGA_SELECTION),
        )
      ) {
        return "optional Vega-Lite spec uses a reserved selection name.";
      }
      if (key === "mark") {
        const mark = typeof child === "string"
          ? child
          : isRecord(child)
            ? child.type
            : undefined;
        if (mark !== "point" && mark !== "line") {
          return "optional Vega-Lite spec supports only point and line marks.";
        }
        markCount += 1;
      }
      const error = visit(child, `${path}.${key}`, depth + 1);
      if (error) return error;
    }
    return undefined;
  };

  const error = visit(spec, "$", 0);
  if (error) return error;
  return markCount > 0
    ? undefined
    : "optional Vega-Lite spec must contain a point or line mark.";
}

function CustomAppRenderer({
  artifactId,
  document,
  onSelectionChange,
}: CustomAppRendererProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const artifactIdRef = useRef(artifactId);
  const documentRef = useRef(document);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const [height, setHeight] = useState(220);
  const [error, setError] = useState<string>();
  const srcDoc = useMemo(() => customAppSrcDoc(document.srcdoc), [document.srcdoc]);

  artifactIdRef.current = artifactId;
  documentRef.current = document;
  onSelectionChangeRef.current = onSelectionChange;

  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow || event.origin !== "null") return;
      const message = bridgeMessageFromRaw(event.data);
      if (!message) return;
      if (message.type === "resize") {
        setHeight(Math.max(180, Math.min(720, Math.round(message.height))));
        return;
      }
      onSelectionChangeRef.current({
        renderer: "custom-app",
        artifactId: artifactIdRef.current,
        ids: [message.selectionId ?? message.componentId],
        componentId: message.componentId,
        interaction: "select",
        value: message.value,
        fidelity: customAppSelectionFidelity(documentRef.current, message),
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (customAppSrcdocBytes(document.srcdoc) > CUSTOM_APP_MAX_SRCDOC_BYTES) {
    return (
      <UnsupportedRenderer
        renderer="custom-app"
        reason={`Custom app exceeds the ${CUSTOM_APP_MAX_SRCDOC_BYTES} byte srcdoc limit.`}
      />
    );
  }

  return (
    <section
      className="scientific-ui-kernel scientific-ui-custom-app"
      data-testid="custom-app-renderer"
      data-entrypoint={document.entrypoint}
      data-renderer-revision={document.revision}
    >
      <div className="scientific-ui-head">
        <div>
          <span className="scientific-kicker">Scientific UI / Custom App</span>
          <h4>{document.title ?? document.id}</h4>
        </div>
        <span
          className="scientific-ui-revision"
          data-testid="scientific-ui-renderer-revision"
        >
          View r{document.revision}
        </span>
        <span className="scientific-ui-root">{document.entrypoint}</span>
      </div>
      {error ? (
        <div className="scientific-ui-fallback" role="alert">{error}</div>
      ) : (
        <iframe
          ref={iframeRef}
          className="scientific-ui-custom-frame"
          title={document.title ?? "Cadence custom scientific app"}
          sandbox="allow-scripts"
          srcDoc={srcDoc}
          style={{ height }}
          onError={() => setError("Custom app could not be loaded.")}
          onLoad={() => {
            iframeRef.current?.contentWindow?.postMessage(
              {
                channel: "cadence.custom-app",
                version: 1,
                type: "lifecycle",
                phase: "mount",
                artifactId,
                documentId: document.id,
              },
              "*",
            );
          }}
        />
      )}
    </section>
  );
}

function UnsupportedRenderer({
  renderer,
  reason,
}: {
  renderer: string;
  reason: string;
}) {
  return (
    <div
      className="scientific-ui-fallback"
      data-testid="scientific-ui-unsupported"
      role="status"
    >
      <strong>Renderer unavailable: {renderer}</strong>
      <span>{reason}</span>
    </div>
  );
}

function customAppSrcDoc(srcdoc: string) {
  const csp =
    '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; script-src \'unsafe-inline\'; style-src \'unsafe-inline\'; img-src data: blob:; font-src data:; connect-src \'none\'; frame-src \'none\'; form-action \'none\'; base-uri \'none\'; object-src \'none\'">';
  const withCsp = srcdoc.includes("<head")
    ? srcdoc.replace(/<head[^>]*>/i, (head) => `${head}${csp}`)
    : `${csp}${srcdoc}`;
  return withCsp.includes("</body>")
    ? withCsp.replace(/<\/body>/i, `${BRIDGE_SCRIPT}</body>`)
    : `${withCsp}${BRIDGE_SCRIPT}`;
}

function customAppSrcdocBytes(srcdoc: string) {
  return new TextEncoder().encode(srcdoc).byteLength;
}

function bridgeMessageFromRaw(raw: unknown): BridgeMessage | undefined {
  if (!isRecord(raw) || raw.channel !== "cadence.custom-app" || raw.version !== 1) {
    return undefined;
  }
  if (
    raw.type === "resize" &&
    typeof raw.height === "number" &&
    Number.isFinite(raw.height)
  ) {
    return { channel: "cadence.custom-app", version: 1, type: "resize", height: raw.height };
  }
  if (
    raw.type === "selection" &&
    typeof raw.componentId === "string" &&
    raw.componentId.length > 0 &&
    raw.componentId.length <= 128 &&
    (raw.selectionId === undefined || typeof raw.selectionId === "string") &&
    (raw.value === undefined || isScientificUiValue(raw.value))
  ) {
    return {
      channel: "cadence.custom-app",
      version: 1,
      type: "selection",
      componentId: raw.componentId,
      selectionId: typeof raw.selectionId === "string" ? raw.selectionId : undefined,
      value: raw.value as ScientificUiValue | undefined,
    };
  }
  return undefined;
}

function initialState(root: ScientificUiNode | undefined): StateMap {
  const state: StateMap = {};
  visitNodes(root, (node) => {
    if (node.type === "Slider" || node.type === "RangeSlider") state[node.id] = node.value;
    if (node.type === "Select") {
      state[node.id] =
        node.options.find((option) => option.id === node.value)?.value ??
        node.value;
    }
    if (node.type === "CheckBox") state[node.id] = node.checked;
  });
  return state;
}

function catalogSelectionFidelity(
  document: ScientificUiArtifactDocument,
  node: ScientificUiNode,
  ids: string[],
  values: StateMap,
  selectedRows: Record<string, string[]>,
): ScientificUiSelectionFidelity {
  const fidelity = descriptorSelectionFidelity(document);
  fidelity.catalogState = {
    controlValues: { ...values },
    selectedRowIds: Object.fromEntries(
      Object.entries(selectedRows)
        .filter(([, rowIds]) => rowIds.length > 0)
        .map(([componentId, rowIds]) => [componentId, [...rowIds]]),
    ),
  };
  if (
    node.type !== "DataTable" &&
    node.type !== "VegaLiteChart" &&
    node.type !== "PlotlyVolcanoChart"
  ) {
    return fidelity;
  }
  fidelity.rowIds = ids.slice(1);
  const source = selectionSourceForNode(node, document);
  if (source) fidelity.source = source;
  return fidelity;
}

function customAppSelectionFidelity(
  document: CustomAppArtifactDocument,
  message: BridgeSelectionMessage,
): ScientificUiSelectionFidelity {
  const fidelity = descriptorSelectionFidelity(document);
  if (message.selectionId !== undefined) {
    fidelity.selectionId = message.selectionId;
  }
  return fidelity;
}

function descriptorSelectionFidelity(
  document: ScientificUiArtifactDocument | CustomAppArtifactDocument,
): ScientificUiSelectionFidelity {
  const fidelity: ScientificUiSelectionFidelity = {
    version: 1,
    descriptorRevision: document.revision,
  };
  if (document.bindings?.artifact_refs.length) {
    fidelity.bindingArtifactRefs = [...document.bindings.artifact_refs];
  }
  return fidelity;
}

function selectionSourceForNode(
  node: ScientificUiSelectableRowsNode,
  document: ScientificUiArtifactDocument,
): NonNullable<ScientificUiSelectionFidelity["source"]> | undefined {
  const binding = dataBindingForNode(node, document);
  const dataRef = dataRefForNode(node, document);
  const artifactRef =
    stringOrUndefined(binding?.artifact_ref) ??
    document.bindings?.artifact_refs.find(
      (ref) =>
        dataRef !== undefined &&
        normalizeArtifactReference(ref) === normalizeArtifactReference(dataRef),
    ) ??
    (dataRef?.startsWith("artifact://") ? dataRef : undefined);
  const artifactRevision =
    numberOrUndefined(binding?.artifactRevision) ??
    numberOrUndefined(binding?.artifact_revision) ??
    numberOrUndefined(binding?.revision);
  if (
    dataRef === undefined &&
    artifactRef === undefined &&
    artifactRevision === undefined
  ) {
    return undefined;
  }
  return { dataRef, artifactRef, artifactRevision };
}

function rowsForNode(
  node: ScientificUiSelectableRowsNode,
  document: ScientificUiArtifactDocument,
  values: StateMap,
  resolveDataRows?: (artifactRef: string) => ScientificUiRow[] | undefined,
): ScientificUiRow[] | undefined {
  const binding = dataBindingForNode(node, document);
  const dataRef = dataRefForNode(node, document);
  const inlineRows = node.type === "PlotlyVolcanoChart" ? undefined : node.rows;
  if (node.type === "PlotlyVolcanoChart" && !dataRef) return undefined;
  const rows = dataRef
    ? resolveDataRows?.(dataRef)
    : inlineRows ??
      (isRecord(binding) && Array.isArray(binding.rows)
        ? rowsFromRaw(binding.rows)
        : undefined);
  if (!rows) return undefined;
  return applyBindingFilters(rows, bindingFilters(binding), values);
}

function dataBindingForNode(
  node: ScientificUiSelectableRowsNode,
  document: ScientificUiArtifactDocument,
): RawRecord | undefined {
  const binding = node.binding
    ? document.bindings?.[node.binding]
    : undefined;
  return isRecord(binding) ? binding : undefined;
}

function dataRefForNode(
  node: ScientificUiSelectableRowsNode,
  document: ScientificUiArtifactDocument,
) {
  const binding = dataBindingForNode(node, document);
  return (
    node.dataRef ??
    stringOrUndefined(binding?.dataRef) ??
    stringOrUndefined(binding?.artifact_ref)
  );
}

function bindingFilters(raw: unknown): ScientificUiFilterBinding[] {
  if (!isRecord(raw) || !Array.isArray(raw.filters)) return [];
  return raw.filters.flatMap((item) => {
    if (
      !isRecord(item) ||
      typeof item.controlId !== "string" ||
      typeof item.column !== "string" ||
      !isFilterOperator(item.op)
    ) {
      return [];
    }
    return [
      {
        controlId: item.controlId,
        column: item.column,
        op: item.op,
      },
    ];
  });
}

function applyBindingFilters(
  rows: ScientificUiRow[],
  filters: ScientificUiFilterBinding[],
  values: StateMap,
) {
  if (!filters.length) return rows;
  return rows.filter((row) =>
    filters.every((filter) =>
      matchesFilter(
        row.values[filter.column],
        filter.op,
        values[filter.controlId],
      ),
    ),
  );
}

function matchesFilter(
  rowValue: ScientificUiValue | undefined,
  op: ScientificUiFilterOperator,
  controlValue: ScientificUiValue | undefined,
) {
  if (rowValue === undefined || controlValue === undefined) return false;
  if (op === "eq" || op === "neq") {
    const equal = scientificUiValuesEqual(rowValue, controlValue);
    return op === "eq" ? equal : !equal;
  }
  if (op === "in") {
    return Array.isArray(controlValue)
      ? controlValue.some((value) => scientificUiValuesEqual(value, rowValue))
      : false;
  }
  if (op === "between") {
    if (!Array.isArray(controlValue) || controlValue.length !== 2) return false;
    const value = Number(rowValue);
    const lower = Number(controlValue[0]);
    const upper = Number(controlValue[1]);
    return (
      Number.isFinite(value) &&
      Number.isFinite(lower) &&
      Number.isFinite(upper) &&
      lower <= value &&
      value <= upper
    );
  }
  const value = Number(rowValue);
  const threshold = Number(controlValue);
  if (!Number.isFinite(value) || !Number.isFinite(threshold)) return false;
  if (op === "gt") return value > threshold;
  if (op === "gte") return value >= threshold;
  if (op === "lt") return value < threshold;
  return value <= threshold;
}

function scientificUiValuesEqual(left: ScientificUiValue, right: ScientificUiValue) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isFilterOperator(value: unknown): value is ScientificUiFilterOperator {
  return ["eq", "neq", "gt", "gte", "lt", "lte", "between", "in"].includes(
    String(value),
  );
}

function catalogDataRefs(document: ScientificUiArtifactDocument) {
  const refs = new Set<string>();
  visitNodes(document.catalog?.root, (node) => {
    if (
      (node.type === "DataTable" ||
        node.type === "VegaLiteChart" ||
        node.type === "PlotlyVolcanoChart") &&
      node.dataRef
    ) {
      refs.add(node.dataRef);
    }
    if (!("binding" in node) || !node.binding) return;
    const binding = document.bindings?.[node.binding];
    if (!isRecord(binding)) return;
    if (typeof binding.dataRef === "string") refs.add(binding.dataRef);
    else if (typeof binding.artifact_ref === "string") {
      refs.add(binding.artifact_ref);
    }
  });
  return [...refs];
}

function normalizeArtifactReference(value: string) {
  const normalized = value.trim();
  return normalized.startsWith("artifact://")
    ? normalized.slice("artifact://".length)
    : normalized;
}

export function scientificUiCatalogFromRaw(
  raw: unknown,
): ScientificUiCatalog | undefined {
  if (!isRecord(raw) || raw.version !== 1) return undefined;
  const root = nodeFromRaw(raw.root);
  return root ? { version: 1, root } : undefined;
}

function nodeFromRaw(raw: unknown): ScientificUiNode | undefined {
  if (!isRecord(raw) || typeof raw.id !== "string" || typeof raw.type !== "string") return undefined;
  const base = { id: raw.id };
  switch (raw.type as ScientificUiPrimitive) {
    case "Row":
    case "Column": {
      if (!Array.isArray(raw.children)) return undefined;
      const children = raw.children.map(nodeFromRaw);
      if (children.some((child): child is undefined => !child)) return undefined;
      const type = raw.type === "Row" ? "Row" : "Column";
      return { ...base, type, children: children as ScientificUiNode[], gap: numberOrUndefined(raw.gap) };
    }
    case "Tabs": {
      if (!Array.isArray(raw.tabs)) return undefined;
      const tabs = raw.tabs.flatMap((item) => {
        if (!isRecord(item) || typeof item.id !== "string" || typeof item.label !== "string") return [];
        const content = nodeFromRaw(item.content);
        return content ? [{ id: item.id, label: item.label, content }] : [];
      });
      return tabs.length
        ? { ...base, type: "Tabs", tabs, defaultTabId: stringOrUndefined(raw.defaultTabId) }
        : undefined;
    }
    case "Text":
      return typeof raw.text === "string" ? { ...base, type: "Text", text: raw.text, tone: toneFromRaw(raw.tone) } : undefined;
    case "Markdown":
      return typeof raw.content === "string" ? { ...base, type: "Markdown", content: raw.content } : undefined;
    case "Metric":
      return (typeof raw.label === "string" && (typeof raw.value === "string" || typeof raw.value === "number"))
        ? { ...base, type: "Metric", label: raw.label, value: raw.value, unit: stringOrUndefined(raw.unit), detail: stringOrUndefined(raw.detail) }
        : undefined;
    case "DataTable": {
      const columns = columnsFromRaw(raw.columns);
      if (!columns) return undefined;
      return {
        ...base,
        type: "DataTable",
        columns,
        rows: rowsFromRaw(raw.rows),
        binding: stringOrUndefined(raw.binding),
        dataRef: stringOrUndefined(raw.dataRef),
      };
    }
    case "Slider":
      return sliderFromRaw(base, raw, false);
    case "RangeSlider":
      return sliderFromRaw(base, raw, true);
    case "Select": {
      const options = optionsFromRaw(raw.options);
      return typeof raw.label === "string" && options
        ? { ...base, type: "Select", label: raw.label, options, value: typeof raw.value === "string" ? raw.value : options[0]?.id ?? "" }
        : undefined;
    }
    case "CheckBox":
      return typeof raw.label === "string" && typeof raw.checked === "boolean"
        ? { ...base, type: "CheckBox", label: raw.label, checked: raw.checked }
        : undefined;
    case "Button":
      return typeof raw.label === "string"
        ? { ...base, type: "Button", label: raw.label, actionId: stringOrUndefined(raw.actionId), payload: isScientificUiValue(raw.payload) ? raw.payload : undefined, variant: raw.variant === "primary" ? "primary" : "quiet" }
        : undefined;
    case "VegaLiteChart": {
      const x = recordFieldFromRaw(raw.x);
      const y = recordFieldFromRaw(raw.y);
      if (!x || !y || (raw.mark !== "point" && raw.mark !== "line")) return undefined;
      const optionalSpec = optionalVegaLiteSpecFromRaw(raw.spec);
      return {
        ...base,
        type: "VegaLiteChart",
        title: stringOrUndefined(raw.title),
        mark: raw.mark,
        x,
        y,
        rows: rowsFromRaw(raw.rows),
        binding: stringOrUndefined(raw.binding),
        dataRef: stringOrUndefined(raw.dataRef),
        ...optionalSpec,
      } as ScientificUiChartRuntimeNode;
    }
    case "PlotlyVolcanoChart": {
      const log2FoldChange = recordFieldFromRaw(raw.log2FoldChange);
      const adjustedPValue = recordFieldFromRaw(raw.adjustedPValue);
      const fdrThreshold = numberOrUndefined(raw.fdrThreshold);
      const foldChangeThreshold = numberOrUndefined(raw.foldChangeThreshold);
      if (
        !log2FoldChange ||
        !adjustedPValue ||
        fdrThreshold === undefined ||
        fdrThreshold <= 0 ||
        fdrThreshold > 1 ||
        foldChangeThreshold === undefined ||
        foldChangeThreshold < 0 ||
        raw.rows !== undefined ||
        (typeof raw.binding !== "string" && typeof raw.dataRef !== "string")
      ) {
        return undefined;
      }
      return {
        ...base,
        type: "PlotlyVolcanoChart",
        title: stringOrUndefined(raw.title),
        log2FoldChange,
        adjustedPValue,
        fdrThreshold,
        foldChangeThreshold,
        binding: stringOrUndefined(raw.binding),
        dataRef: stringOrUndefined(raw.dataRef),
      };
    }
    case "ArtifactViewer":
      return typeof raw.artifactRef === "string"
        ? { ...base, type: "ArtifactViewer", artifactRef: raw.artifactRef, title: stringOrUndefined(raw.title) }
        : undefined;
    default:
      return undefined;
  }
}

function sliderFromRaw(
  base: { id: string },
  raw: RawRecord,
  range: boolean,
): ScientificUiNode | undefined {
  if (
    typeof raw.label !== "string" ||
    typeof raw.min !== "number" ||
    typeof raw.max !== "number" ||
    !Number.isFinite(raw.min) ||
    !Number.isFinite(raw.max)
  ) return undefined;
  if (range) {
    if (!Array.isArray(raw.value) || raw.value.length !== 2 || !raw.value.every((value) => typeof value === "number")) return undefined;
    return { ...base, type: "RangeSlider", label: raw.label, min: raw.min, max: raw.max, step: numberOrUndefined(raw.step), value: [raw.value[0], raw.value[1]], unit: stringOrUndefined(raw.unit) };
  }
  return typeof raw.value === "number"
    ? { ...base, type: "Slider", label: raw.label, min: raw.min, max: raw.max, step: numberOrUndefined(raw.step), value: raw.value, unit: stringOrUndefined(raw.unit) }
    : undefined;
}

function columnsFromRaw(raw: unknown): ScientificUiColumn[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const columns = raw.flatMap((item) => isRecord(item) && typeof item.id === "string" && typeof item.label === "string" ? [{ id: item.id, label: item.label }] : []);
  return columns.length ? columns : undefined;
}

function optionsFromRaw(raw: unknown) {
  if (!Array.isArray(raw)) return undefined;
  const options = raw.flatMap((item) => isRecord(item) && typeof item.id === "string" && typeof item.label === "string" ? [{ id: item.id, label: item.label, value: isScientificUiValue(item.value) ? item.value : item.id }] : []);
  return options.length ? options : undefined;
}

function rowsFromRaw(raw: unknown): ScientificUiRow[] | undefined {
  if (!Array.isArray(raw) || raw.length > SCIENTIFIC_UI_MAX_INLINE_ROWS) return undefined;
  const seenIds = new Set<string>();
  const rows = raw.flatMap((item) => {
    if (
      !isRecord(item) ||
      typeof item.id !== "string" ||
      !SCIENTIFIC_UI_STABLE_ID.test(item.id) ||
      seenIds.has(item.id) ||
      !isRecord(item.values)
    ) {
      return [];
    }
    seenIds.add(item.id);
    return isScientificUiValue(item.values) ? [{ id: item.id, values: item.values as Record<string, ScientificUiValue> }] : [];
  });
  return rows.length === raw.length && seenIds.size === raw.length ? rows : undefined;
}

function recordFieldFromRaw(raw: unknown) {
  if (!isRecord(raw) || typeof raw.field !== "string") return undefined;
  return { field: raw.field, label: stringOrUndefined(raw.label) };
}

function bindingsFromRaw(raw: unknown): ScientificUiBindings | undefined {
  if (!isRecord(raw) || !Array.isArray(raw.artifact_refs) || !raw.artifact_refs.every((ref) => typeof ref === "string")) return undefined;
  return raw as ScientificUiBindings;
}

function sandboxFromRaw(raw: unknown) {
  if (!isRecord(raw) || !Array.isArray(raw.allow) || !raw.allow.includes("scripts")) return undefined;
  if (raw.allow.includes("same-origin") || raw.allow.includes("top-navigation") || raw.allow_same_origin !== false || raw.allow_top_navigation !== false) return undefined;
  return true;
}

function visitNodes(root: ScientificUiNode | undefined, visitor: (node: ScientificUiNode) => void) {
  if (!root) return;
  visitor(root);
  if (root.type === "Row" || root.type === "Column") root.children.forEach((child) => visitNodes(child, visitor));
  if (root.type === "Tabs") root.tabs.forEach((tab) => visitNodes(tab.content, visitor));
}

function displayValue(value: ScientificUiValue | undefined) {
  if (value === undefined || value === null) return "";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

function isRecord(value: unknown): value is RawRecord {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isScientificUiValue(value: unknown, depth = 0): value is ScientificUiValue {
  if (depth > 5) return false;
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.length <= 100 && value.every((item) => isScientificUiValue(item, depth + 1));
  return isRecord(value) && Object.keys(value).length <= 100 && Object.values(value).every((item) => isScientificUiValue(item, depth + 1));
}

function numberOrUndefined(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function stringOrUndefined(value: unknown) {
  return typeof value === "string" ? value : undefined;
}

function toneFromRaw(value: unknown): ScientificUiTextNode["tone"] {
  return value === "muted" || value === "accent" ? value : "default";
}
